import React, { useState, useEffect, useContext } from 'react';
import { canvasOption } from '@/pages/intelligence/utils/radiusImg';
import appstoreIcon from '@/pages/intelligence/assets/svg/dataSource/appstore.svg';
import googleImg from '@/pages/intelligence/assets/svg/dataSource/googleplay.svg';
import ChartBubble from '@/pages/intelligence/components/ChartBubble';
import { BAR_GRID_TOP } from '@/pages/intelligence/pages/TopCharts/_config/echarts';
import { ThemeContext } from '@/pages/home/wrappers/common';

interface Props {
  barOrPieData: any[];
  loading: boolean;
  segVal: string;
  cardWidth: number;
  dateType: string;
}

export default function useDemographicsBarCharts(props: Props) {

  // 获取颜色主题值，通过ThemeContext消费者useContext
  const { themeValue } = useContext(ThemeContext);
  const { barOrPieData, loading, segVal, cardWidth, dateType } = props;
  const [barSeries, setBarSeries] = useState<any[]>([]);
  const [barxAxis, setBarxAxis] = useState<any[]>([]);
  const [loadingEchart, setLoadingEchart] = useState(true);
  const [iconOption, setIconOption] = useState<Record<string, any>>({});

  //默认浮窗内容
  const lineFormatterTooltip = (params: any[]) => {
    return ChartBubble({
      data: params,
      headerLeftTitle: segVal === 'gender' ? 'Gender' : 'Age',
      titleNow: 'Demographics%',
      chartType: 'line',
      dateType,
      ifNoBarImg: true,
      isPercent: true,
      topChartsType: 'top_demographics',
    });
  };

  const xAxisCfg = {
    type: 'category',
    position: 'bottom',
    fontSize: 12,
    rotate: 30,
    axisTick: {
      alignWithLabel: true, //x轴刻度线与标签对齐
      lineStyle: {
        color: '#BFBFBF',
        //透明度会展示到虚线，暂时用纯色代替
        // color: '#000',
        // opacity: 0.25,
        width: 1,
      },
    },
    axisLine: {
      show: true,
      // onZero: false,//当y轴有负数时,让x轴下落在y轴最小值
      lineStyle: {
        // color: '#BFBFBF',
        //透明度会展示到虚线，暂时用纯色代替
        // color: '#000',
        // opacity: 0.25,
        width: 1,
      },
    },
    boundaryGap: true,
  };

  const option = {
    grid: {
      left: '40px',
      right: '0px',
      // top: '25px',
      top: BAR_GRID_TOP,
      bottom: '78px',
    },
    tooltip: {
      trigger: 'axis',
      lineStyle: {
        color: 'rgba(0,0,0,0.25)',
        width: 2,
        type: 'solid',
      },
      showDelay: 0, // 显示延迟，添加显示延迟可以避免频繁切换，单位ms
      axisPointer: {
        type: 'shadow',
        shadowStyle: {
          color: '#96c5eb',
          opacity: '0.01',
          width: 'auto',
        },
        label: {
          backgroundColor: '#6a7985',
        },
      },
      padding: 0,
      borderWidth: 0,
      // 进行数据处理,去掉提示框中一些x轴坐标为空的数据显示.
      formatter: function (datas: any) {
        if (!Array.isArray(datas)) {
          return datas;
        }
        //特殊处理一下数据
        const data = datas?.filter((t) => {
          return t.value !== undefined;
        });
        return lineFormatterTooltip(data);
      },
      backgroundColor: 'rgba(0,0,0,0)'
    },
    legend: {
      show: false,
    },
    yAxis: [
      {
        type: 'value',
        splitLine: {
          lineStyle: {
            type: 'dashed',
            color: '#E5E5E5',
          },
        },
        axisLabel: {
          color: ' #000000',
          fontSize: 12,
          opacity: 0.45,
          formatter: function (value: number) {
            return value + '%';
          },
        },
      },
    ],
    xAxis: {}, // barxAxis,
    series: [], // barSeries,
  };
  const [options, setOptions] = useState(option);

  const gameIDRex = (id: string) => {
    if (typeof id === 'string') {
      return id.replace(/[^a-z0-9]+/gi, '');
    }
    return id;
  };

  const getSeriesData = (list: any[], segVal: string, imageUrl: any = {}) => {
    const newSeries: any = [];
    const newxAxis: any[] = [];
    /**计算可视化X轴宽度 */
    // let cardWidth = cardRef?.current?.clientWidth;
    let barItemWidth = 0;
    if (cardWidth) {
      barItemWidth = (cardWidth - 100) / list.length;
    }

    list?.map((t, index) => {
      //游戏列表遍历
      let xAxisIndexData = new Array(list.length).fill('');
      xAxisIndexData[index] = t?.name + '_platform' + t?.platform;
      const id = gameIDRex(t?.app_id);
      newxAxis.push({
        ...xAxisCfg,
        /**X轴富文本 */
        axisLabel: {
          interval: 0,
          width: barItemWidth ? barItemWidth : undefined,
          overflow: 'truncate',
          truncate: '...',
          formatter: function (value: string) {
            if (!value) {
              return '';
            }
            const [name, platfrom] = value?.split('_platform');
            return [`{${id}| \n}`, `{${platfrom}| } ${name}`].join('\n');
            // return '{' + id + '| }\n\n{id|' + name + '}';
          },
          margin: 20,
          rich: {
            ios: {
              align: 'center',
              width: 16,
              height: 16,
              backgroundColor: {
                image: appstoreIcon,
              },
            },
            android: {
              align: 'center',
              width: 16,
              height: 16,
              backgroundColor: {
                image: googleImg,
              },
            },
            [id]: {
              align: 'center',
              width: 30,
              height: 30,
              backgroundColor: !imageUrl[id]
                ? '#fff'
                : {
                  image: imageUrl[id],
                },
            },
          },
        },
        data: xAxisIndexData,
      });

      const metricList =
        segVal === 'age'
          ? t.ageMetricList
          : segVal == 'gender'
            ? t.genderMetricList
            : [];
      metricList?.map((metric: any) => {
        //数据遍历
        let metricIndexData = new Array(index + 1).fill({});
        metricIndexData[index] = {
          itemStyle: {
            color: metric?.color,
          },
          value: parseFloat(metric?.value),
        };
        newSeries.push({
          name: metric?.name,
          type: 'bar',
          xAxisIndex: index,
          data: metricIndexData,
          barGap: '2%', //不同系列的柱间距离
          barMaxWidth: 40, //TODO
        });
      });
    });
    setBarSeries(newSeries);
    setBarxAxis(newxAxis);
    setOptions({
      ...option,
      xAxis:
        newxAxis?.length > 0
          ? newxAxis
          : {
            ...xAxisCfg,
          },
      series: newSeries,
    });
  };

  const awaitChartIconUndateSeries = async (list: any[], themeValue: boolean) => {
    let imageUrl: any = {};
    try {
      list?.map((item, index) => {
        const id = gameIDRex(item?.app_id);
        imageUrl[id] = item?.cover /*|| GameDefault*/;
      });
      let iconOption = await canvasOption(imageUrl, 51.2, 51.2, 8, themeValue, false);
      setIconOption({ ...iconOption });
    } catch (err) {
      console.error(err);
    }
    setLoadingEchart(false);
  };

  //给X轴更新cavas画好的图片
  useEffect(() => {
    let xAxis = options.xAxis;
    if (Array.isArray(xAxis) && JSON.stringify(iconOption) !== '{}') {
      const ids = Object.keys(iconOption);
      let cavasRich: any = {};
      ids.map((id) => {
        cavasRich[id] = {
          align: 'center',
          width: 30,
          height: 30,
          backgroundColor: !iconOption[id]
            ? '#fff'
            : {
              image: iconOption[id],
            },
        };
      });
      //给x轴更新画好的图片
      let iconXaxis = xAxis.map((item) => {
        let axisLabel = item?.axisLabel;
        axisLabel.rich = {
          ...axisLabel.rich,
          ...cavasRich,
        };
        return {
          ...item,
          axisLabel: axisLabel,
        };
      });

      setOptions({
        ...options,
        xAxis: iconXaxis,
      });
    }
  }, [iconOption]);

  const [isFirst, setIsFirst] = useState(true);
  useEffect(() => {
    // if (loading) {
    //   return;
    // }
    setLoadingEchart(true);
    getSeriesData(barOrPieData, segVal, iconOption);
    //画cavas可视化游戏图片
    awaitChartIconUndateSeries(barOrPieData, themeValue);
    if (isFirst) {
      setLoadingEchart(false);
      setIsFirst(false);
    }
  }, [barOrPieData, loading, segVal, cardWidth, themeValue]);

  return {
    options,
  };
}
